import ErrorNote from './ErrorNote'
import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchContent } from '../api/content'
import { CalendarOff } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useCalendarFilter, withFilter } from '../useCalendarFilter'
import { bookingKindLabel, bookingMatches, fetchBookings, type BookedSlot } from '../api/bookings'
import { bookingIsOff, lessonSessionOf, withoutShownPrivates } from '../api/privateLessons'
import {
  addDays,
  explainEmptyWeek,
  shouldExplainEmptyWeek,
  fetchFilterOptions,
  fetchWeek,
  formatDayLabel,
  formatFullDate,
  formatRange,
  formatTime,
  instructorNames,
  isCovered,
  canOpenRoster,
  myPartIn,
  meridiem,
  today,
  type CalendarEntry,
  type EmptyWeek,
} from '../api/calendar'
import { describeClass } from '../api/classDescription'
import { useCan, useSession } from '../useSession'
import CalendarFilters from './CalendarFilters'
import { type CalendarView } from './CalendarViewSwitcher'
import BookingForm from './BookingForm'
import ScheduleHeader from './ScheduleHeader'
import CalendarLink from './CalendarLink'
import RosterModal from './RosterModal'
import { PracticeLinks } from './PracticeLinks'

/**
 * Colour by room, matching how the studio already colour-codes its space
 * (1-Purple, 4-Blue, and so on). More useful than colouring by style, because
 * "where is this happening" is the question a schedule is usually read for.
 *
 * Deliberately never the accent, which means "interactive" everywhere else.
 * Falls back to a neutral so an unassigned class stays legible rather than
 * invisible.
 */
function entryColour(entry: CalendarEntry): string {
  return entry.roomColor ?? 'var(--color-ink-3)'
}

/**
 * A room booking on the week grid (D-372).
 *
 * **Reported: an approved private was nowhere on a teacher's schedule.** The
 * calendar knows nothing about bookings — `CalendarService` has no reference to
 * `RoomBooking` at all — and only `CalendarDayView` overlaid them, from
 * `/api/bookings`. Her schedule opens on the *week*, so a private was invisible
 * until she switched to Day: three views of one calendar and one of them knew.
 *
 * **Dashed rather than solid, still.** A booking is a reservation, not a lesson
 * (D-80), and the eye should tell them apart before the words do.
 *
 * **But it opens now, and it says what it is** (D-388). The original note said
 * a button would be "a control that refuses", which was true while the only
 * affordance on offer was a roster a booking does not have. Editing and
 * cancelling are real actions on a booking, and until this they were reachable
 * from the *day* view alone — so a teacher looking at her own week could see her
 * private and had nowhere to click. Reported as: *"There should be a way to edit
 * or delete a private from a teachers account."*
 *
 * It also never said what kind of thing it was, which is the other half of
 * *"why do they look different?"* — a private lesson and a held room rendered
 * identically, distinguished only by whether a name happened to be on them.
 */
function BookingCard({ slot, onOpen }: { slot: BookedSlot; onOpen?: (slot: BookedSlot) => void }) {
  const start = formatTime(slot.startTime)

  // Who is in it, or how many when nobody is named — a held room with no dancer
  // is `SDT SPOT - ________` in the real data and must still read as something
  // (D-80).
  const who = slot.dancerNames.length > 0 ? slot.dancerNames.join(', ') : slot.sizeLabel

  const body = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="tabular text-xs font-semibold" style={{ color: 'var(--room-display)' }}>
          {start ? `${start}${meridiem(slot.startTime)}` : 'Time TBD'}
        </span>
        <span className="text-[11px] font-semibold text-ink-3">{slot.roomName}</span>
      </div>

      {/*
        Struck through when it is off (D-648), the same treatment a cancelled
        class already gets. It used to be filtered off the grid entirely, so a
        mistap simply vanished and the undo had nowhere to be pressed from.
      */}
      <h3
        className={`mt-1 text-[13px] leading-snug font-semibold wrap-anywhere ${
          bookingIsOff(slot) ? 'text-ink-3 line-through' : 'text-ink'
        }`}
      >
        {who}
      </h3>

      {/*
        What kind of thing this is, which the card never said. Without it a
        private lesson and a held room are the same card with a different name
        on it — and a teacher scanning her week cannot tell a lesson from a
        rehearsal she booked (D-388).
      */}
      <p className="mt-0.5 text-[11.5px] text-ink-3">
        {bookingKindLabel(slot.kind)} &middot; {slot.staffName}
      </p>
    </>
  )

  const skin =
    'room-tint w-full rounded-lg border border-dashed border-line bg-surface-2 p-2.5 text-left'

  const style = {
    '--room-color': slot.roomColor ?? 'var(--color-ink-3)',
    borderLeft: '3px dashed var(--room-display)',
  } as React.CSSProperties

  /*
   * A button only for somebody who may actually open it. The same rule the
   * class cards follow, and the reason this is decided here rather than in the
   * browser: a card that opens a form the server then refuses is worse than a
   * card that never offered (`CLAUDE.md`).
   */
  if (!onOpen) {
    return (
      <div className={skin} style={style}>
        {body}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(slot)}
      className={`${skin} transition-colors hover:border-ink-3`}
      style={style}
    >
      {body}
    </button>
  )
}

function ClassCard({
  entry,
  onOpen,
  weekMixes,
}: {
  entry: CalendarEntry
  onOpen: (occurrenceId: string) => void
  /** Her own scope with `teachingOnly` and the studio view both off (D-539). */
  weekMixes: boolean
}) {
  // The studio's word for the link, shared with the family portal so the two
  // never drift (D-541); the query is the same cached one every screen reads.
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const start = formatTime(entry.startTime)

  /*
   * **The reader is read here; the week is threaded in** (D-539). Who she is
   * comes off the session, which any card can ask. Whether the week she is
   * looking at is her mixed one — `teachingOnly` off, the studio view off —
   * only the week view knows, because only it holds the filter. An audit found
   * the first version keying on the reader alone: with "Show studio schedule"
   * on, every one of opening night's sessions read *Dancing*.
   */
  const { session } = useSession()
  const myPart = myPartIn(entry, session ?? {}, weekMixes)

  /*
   * **A card only offers a roster the server would give** (D-539). A plain
   * dancer holds no `ViewPeople`; a dancer on the staff may read only the
   * classes she is on. For either, her own Tuesday ballet was a button that
   * answered with a permission box or `Roster request failed (404)`.
   */
  const mayOpen = canOpenRoster(entry.instructors.map((i) => i.id), session ?? {})
  const colour = entryColour(entry)

  /*
   * **One parser for the studio's own names** (D-579, applying D-190).
   *
   * `describeClass` is the single place that knows a Jackrabbit name is
   * `STYLE | DAY TIME | AGE | SIZE | LEVEL | TEACHER`, and it has read them
   * since the Today panel was built. Calling it here rather than writing a
   * second reading of the same string is the whole of why this is cheap.
   *
   * No routine name: `CalendarEntry` does not carry one — only the class view
   * does (D-319) — so a routine titled *Golden Huntrix* still shows its style
   * here and its own name where that is known.
   */
  const { label, detail, who } = describeClass(entry.className)

  // *Solo · Hannah*, which is what tells two identical-looking cards apart.
  const named = who ? `${label} \u00b7 ${who}` : label

  /*
   * Teachers and, for a recreational class, what it actually covers - *Tippy
   * Toes* alone says nothing, *Tippy Toes / Hannah Jones · ballet, tap,
   * tumbling* says all of it in the line the room used to occupy.
   */
  const secondary = [
    entry.instructors.length > 0 ? instructorNames(entry.instructors) : null,
    detail,
  ]
    .filter(Boolean)
    .join(' \u00b7 ')

  return (
    // A wrapper, so the practice-video link (D-541) can be a real link beside
    // the card rather than an <a> nested in a <button> - invalid HTML whose
    // click would also open the roster.
    <div>
    {/*
      A button, not an article: the week is the view people land on, and
      "who is in this?" should be answerable here rather than only after
      navigating into a day.
    */}
    <button
      type='button'
      disabled={!mayOpen}
      aria-disabled={!mayOpen}
      onClick={() => { if (mayOpen) onOpen(entry.occurrenceId) }}
      title={mayOpen ? `${entry.className} - click for the roster` : entry.className}
      className="room-tint w-full rounded-lg border border-line bg-surface p-2.5 text-left transition-[filter] hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
      style={
        {
          '--room-color': colour,
          borderLeft: '3px solid var(--room-display)',
        } as React.CSSProperties
      }
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="tabular text-xs font-semibold" style={{ color: 'var(--room-display)' }}>
          {start ? `${start}${meridiem(entry.startTime)}` : 'Time TBD'}
        </span>
        {entry.capacity !== null && (
          <span
            className={`tabular text-[11px] font-semibold ${
              entry.enrolled >= entry.capacity ? 'text-crit' : 'text-ink-3'
            }`}
          >
            {entry.enrolled}/{entry.capacity}
          </span>
        )}
      </div>

      {/*
        **The name as a person would say it** (D-579).

        The raw Jackrabbit string was printed here, wrapped anywhere it had to
        be: `COMP_LYR | TU 400 | 9/10 | SMALL | COMP | MADDIE` is four lines of
        a week column on its own, and `COMP_TECH_CONDITIONING` is one
        unbreakable word to CSS because an underscore offers no break
        opportunity. Measured at a real column width, the card was **104px**;
        this is 71.

        `describeClass` already turns that string into *Lyrical*, and has since
        D-190 — it runs the Today panel and had never been pointed at the grid
        that most needed it.

        **The dancer's name is what keeps this honest.** The comment here used
        to say *"the name is the only thing distinguishing two cards in the same
        room at the same hour"*, and it was right: two solos at 5:30 in 6-Black
        would both read *Solo*. So the label carries `who` beside it — *Solo ·
        Hannah* against *Solo · Onnika* — which is exactly the part of the
        Jackrabbit name that tells them apart, and the part that used to sit at
        the end of the fourth line where nobody read it.

        `wrap-anywhere` stays. A parsed label is short, but *Musical theatre*
        with three dancers is not, and the tokens can still arrive unparsed
        when a name does not match any shape the parser knows.

        The full original is still one hover away, in `title` above.
      */}
      <h3 className="mt-1 text-[13px] leading-snug font-semibold wrap-anywhere text-ink">
        {named}
      </h3>

      <div className="mt-1 space-y-0.5 text-[11.5px] wrap-anywhere text-ink-3">
        {/*
          **The room is not said in words** (D-579). The card is tinted with
          the room's colour and the time above is printed in it, so this line
          was the third telling — and the one costing a line of every card in
          the grid. The colour is the studio's own vocabulary: the rooms *are*
          named after colours.
        */}
        {secondary && <div>{secondary}</div>}

        {/*
          **Who is standing in, said on the class rather than in a list beneath
          the week** (D-365).

          `CoverPanel` carried a *"You are covering this one"* card for every
          session she had agreed to take; deleting it took the only forward
          view of that with it, and the calendar had `isSubstitute` on every
          entry and rendered it nowhere. On the class is where it belongs — she
          reads the week to know where to be, and *whose* class it is changes
          nothing about that except that it is not normally hers.

          Not the accent, which means *interactive* everywhere else in this
          grid, and not the room colour, which is the one signal a card already
          carries (D-176).
        */}
        {isCovered(entry.instructors) && (
          <div className="font-semibold text-accent-2">Covered</div>
        )}

        {/*
          **Which way this one is hers** (D-539).

          Only for somebody whose week holds both — a dancer here who is also on
          the staff. `myPartIn` returns null for everybody else, so no teacher
          gains a badge saying "teaching" twelve times.

          It sits with the room and the names rather than above them, because it
          is the least of the three facts: she reads the week to know where to
          be, and *how* the class is hers only settles what she does when she
          gets there.
        */}
        {myPart && <div className="font-semibold text-ink-2 capitalize">{myPart}</div>}
      </div>

      {entry.isCancelled && (
        <div className="mt-1.5 rounded bg-crit-bg px-1.5 py-0.5 text-[11px] font-semibold text-crit">
          Cancelled{entry.cancellationReason ? `: ${entry.cancellationReason}` : ''}
        </div>
      )}
    </button>

    {/*
      The practice video (D-541), **only for somebody who cannot open the
      class** (D-707). `asked`: *"i think the video link should just exist
      inside the class not on the schedule"* - for a teacher it sat under the
      card, outside its frame, repeating what the class's own Routine panel
      already shows. A dancer signed in as herself is the exception: the roster
      is refused her, so this card is her only per-class surface (D-539), and
      removing it here would take the link away from her altogether. Families
      read it on their portal row, which this does not touch.
    */}
    {!mayOpen && (
      <PracticeLinks
        classId={entry.classId}
        musicUrl={entry.musicUrl}
        choreoLinks={entry.choreoLinks}
        content={content}
      />
    )}
    </div>
  )
}

/**
 * Says why a week is empty, and offers the way out.
 *
 * Every branch names a date rather than a direction. "Nothing this week" leaves
 * the reader to go hunting; "the first class is Tuesday 8 September" answers the
 * question and hands them a button.
 */
function EmptyWeekNotice({ reason, onJump }: { reason: EmptyWeek; onJump: (date: string) => void }) {
  const copy: { title: string; body: string; jumpTo?: string; jumpLabel?: string } =
    reason.kind === 'filtered'
      ? {
          title: 'Nothing matches these filters',
          body: `${reason.hidden} ${reason.hidden === 1 ? 'session is' : 'sessions are'} scheduled this week — clear a filter to see them.`,
        }
      : reason.kind === 'before-season'
        ? {
            // Worded around the apostrophe rather than spelling it. This file
            // has been silently re-encoded to ANSI more than once and a curly
            // quote is the first casualty; no glyph, no risk.
            title: 'The season has not started yet',
            body: `The first class is ${formatFullDate(reason.jumpTo)}.`,
            jumpTo: reason.jumpTo,
            jumpLabel: 'Go to the first week',
          }
        : reason.kind === 'after-season'
          ? {
              title: 'The season has finished',
              body: `The last class was ${formatFullDate(reason.jumpTo)}.`,
              jumpTo: reason.jumpTo,
              jumpLabel: 'Go to the last week',
            }
          : reason.kind === 'break'
            ? {
                title: 'No classes this week',
                body: `A break in the season — classes pick up again ${formatFullDate(reason.jumpTo)}.`,
                jumpTo: reason.jumpTo,
                jumpLabel: 'Skip to the next week with classes',
              }
            : {
                title: 'Nothing is scheduled yet',
                body: 'No classes exist for any date. Import from Jackrabbit, or add a class to get started.',
              }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-4 rounded-2xl border border-line bg-surface px-5 py-4">
      <CalendarOff size={20} strokeWidth={2} className="shrink-0 text-ink-3" />

      {/*
        `basis-[16rem]`, and without it this panel is broken on a phone —
        reported with a screenshot, and it is D-310's bug in a second component.

        The row is `flex-wrap`, the button is `shrink-0`, and this child was
        `flex-1` with the default `basis-0`. **A zero basis means the line
        always "fits" by construction**, so the row never wraps — and the
        shortfall is taken entirely out of the only child allowed to shrink.
        "No classes this week" came out one word per line beside a button at
        its full width.

        A real basis makes the row genuinely too wide on a narrow screen, which
        is what `flex-wrap` needs in order to do anything.
      */}
      <div className="min-w-0 flex-1 basis-[16rem]">
        <p className="text-[14px] font-semibold text-balance">{copy.title}</p>
        <p className="mt-0.5 text-[13px] text-ink-3">{copy.body}</p>
      </div>

      {copy.jumpTo && (
        <button
          type="button"
          onClick={() => onJump(copy.jumpTo!)}
          className="shrink-0 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent hover:brightness-110"
        >
          {copy.jumpLabel}
        </button>
      )}
    </div>
  )
}

export default function CalendarWeekView({
  date: anchor,
  onDate: setAnchor,
  onView,
  actions,
  teachingOnly = false,
}: {
  /**
   * The week on screen, owned by the caller (D-360).
   *
   * **Controlled, with no internal fallback.** It held its own `useState`
   * seeded from an `initialDate`, which meant nothing outside could know which
   * week was showing once a single arrow had been tapped — the teacher portal's
   * copy of the anchor went stale immediately, because paging never reported
   * upward. That is what made *"carry the date range to the other tab"*
   * impossible rather than merely unbuilt.
   */
  date: string
  onDate: (date: string) => void
  /**
   * What a view change does. **Required** (D-395).
   *
   * D-312 made this optional so the teacher portal could switch in place, and
   * left the absent case routing to `/calendar/*` from inside the switcher —
   * routes gated on `ViewAllSchedules`, which is the capability an embed exists
   * to work around. So an embed that forgot the prop offered a parent three
   * controls landing on *"Not your page"*, from an omission nothing at the call
   * site could show.
   *
   * Required now, so a caller cannot forget. The route wrappers navigate, the
   * same way they already answer `onDate` — which is where the route is known
   * and where the capability is already guaranteed by the guard.
   */
  onView: (view: CalendarView, date: string) => void
  /**
   * Buttons that belong with the date controls rather than above them (D-321).
   *
   * "Book a room" started life as its own row over the calendar and read as a
   * banner. It is the same kind of thing as Week/Day/Month — something you do
   * to this view — so it sits in the same line, to the left of the switcher.
   */
  actions?: ReactNode

  /**
   * Show only what she teaches, leaving her own children's classes out
   * (D-374).
   *
   * Set by `TeacherSchedule` for *My classes*. Off everywhere else, so the
   * master calendar and the parent half are unchanged — and off is the union,
   * which is what home needs and what D-352's tests pin.
   */
  teachingOnly?: boolean
}) {
  const [rosterId, setRosterId] = useState<string | null>(null)

  /*
   * Editing a booking from the week, not only from the day (D-388). Gated on
   * the same capability the endpoint requires, so a card is offered to exactly
   * the people the server will accept a change from.
   */
  const [editingBooking, setEditingBooking] = useState<BookedSlot | null>(null)
  const canBookRooms = useCan('BookRooms')

  /*
   * A private's card is the lesson (D-733): it opens the lesson's attendance,
   * where the booking can be changed, for anybody who may open that roster.
   * Every other booking opens its form, as before, for somebody who may book.
   */
  const openBooking = (slot: BookedSlot) => {
    const lesson = lessonSessionOf(slot)
    if (lesson && canOpenRoster([slot.staffId], session ?? {})) {
      setRosterId(lesson)
      return
    }
    if (canBookRooms) setEditingBooking(slot)
  }
  const mayOpenBooking = (slot: BookedSlot) =>
    canBookRooms || (lessonSessionOf(slot) !== null && canOpenRoster([slot.staffId], session ?? {}))
  const editBookingById = (bookingId: string) => {
    const slot = (bookings ?? []).find((b) => b.id === bookingId)
    if (!slot) return
    setRosterId(null)
    setEditingBooking(slot)
  }

  const { session } = useSession()
  const seesEverything = useCan('ViewAllSchedules')
  const [urlFilter, setFilter] = useCalendarFilter()
  const [searchParams] = useSearchParams()

  /*
   * **Which half of her schedule, folded into the filter** (D-374).
   *
   * The caller says it — `TeacherSchedule` renders this for *My classes* — but
   * it has to travel *inside* the filter rather than beside it, because the
   * react-query key is built from the filter. Held outside, switching tabs
   * would serve the other half's cached week and the narrowing would appear not
   * to work.
   */
  const filter = teachingOnly ? { ...urlFilter, teachingOnly: true } : urlFilter

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['calendar', 'week', anchor, filter],
    queryFn: () => fetchWeek(anchor, filter),
    // Keeps the previous week on screen while a filter change is in flight, so
    // the grid does not blink to empty on every keystroke.
    placeholderData: (previous) => previous,
  })

  /*
   * Options come from the unfiltered week, so the controls never offer a filter
   * that would return nothing — but they follow the *view* flags, or they
   * describe a different calendar from the grid beside them (D-299, D-374).
   */
  const view = { wholeStudio: filter.wholeStudio, teachingOnly: filter.teachingOnly }
  const { data: options } = useQuery({
    queryKey: ['calendar', 'filters', anchor, view],
    queryFn: () => fetchFilterOptions(anchor, view),
  })

  /*
   * Bookings ride alongside, because the calendar itself has none (D-372).
   *
   * Keyed on the week the payload actually returned rather than on `anchor`,
   * so the two can never describe different weeks — `fetchWeek` decides where
   * the week starts, and guessing it again here is how a grid ends up showing
   * Monday's classes beside Sunday's privates.
   *
   * A failure here leaves the classes alone. A missing overlay is a quieter
   * wrong than a week that will not draw (D-209).
   */
  const { data: bookings } = useQuery({
    queryKey: ['bookings', data?.weekStart, data?.weekEnd],
    queryFn: () => fetchBookings(data!.weekStart, data!.weekEnd),
    enabled: Boolean(data?.weekStart && data?.weekEnd),

    // The same call the week above makes, and for a sharper reason: paging
    // changes this key to one with no cache, so without it the cards vanish for
    // a render and the empty-week notice flashes in and out on a quiet week.
    placeholderData: (previous) => previous,
  })

  const empty = data ? explainEmptyWeek(data) : null

  /*
   * **Computed once, because two copies of this disagreed** (D-372).
   *
   * The day columns filtered cancelled bookings out and the notice guard
   * counted them in, so a week whose only booking had been called off rendered
   * *"Nothing on"* in every column **and** suppressed the notice explaining
   * why — a silent screen with no way forward, reachable by cancelling one
   * private out of season.
   */
  /*
   * **Scoped the same way the classes beside them are** (D-373, amending
   * D-372).
   *
   * D-372 put bookings on this grid unscoped, because the client had no way to
   * know which staff member was reading it. That left one screen showing *her*
   * classes beside *the studio's* privates, with the "Show studio schedule"
   * toggle — unticked — describing only half of it. Once `Session` carries a
   * `staffId` the overlay can honour the same switch: her own by default, the
   * whole building when she asks for it.
   *
   * **`ViewAllSchedules` sees everything regardless**, because for that reader
   * this *is* the studio timetable and the toggle is hidden from her (D-300).
   * Somebody with no `staffId` and no such capability holds no bookings, so the
   * filter correctly leaves her with none.
   */
  /*
   * <b>A cancelled private stays on the grid</b> (D-648), struck through, the
   * way a cancelled class already does.
   *
   * It was filtered out entirely - so a lesson called off by mistake simply
   * vanished, and the undo added with D-648 had nowhere to be pressed from. It
   * also read as two different rules for one idea: a cancelled class is history
   * the studio can see, a cancelled private was gone.
   *
   * The room it held is still free either way; `hideCancelled` is the reader's
   * own switch and governs both (D-355).
   */
  const held = (bookings ?? [])
    .filter((b) => !filter.hideCancelled || !bookingIsOff(b))
    .filter((b) => seesEverything || filter.wholeStudio || b.staffId === session?.staffId)
    /*
     * <b>Privates only</b> (D-649). `asked`: *"Teachers want a way in their
     * calendar to quickly see just the privates they are scheduled for."* A
     * held room with no dancer and a rehearsal are both bookings and neither is
     * a private, so this narrows by kind rather than by being a booking at all.
     */
    .filter((b) => !filter.privatesOnly || b.kind === 'PrivateLesson')
    /*
     * <b>And the filter chips reach the privates too</b> (D-770).
     *
     * `asked`, from QA: *"when adding a filter by a teacher, it doesn't seem to
     * be showing just that teachers classes ... seems to be showing other
     * privates and things not associated with them."*
     *
     * The classes are narrowed by the server; the privates are a second
     * request the filter never reached (D-372), so a chip reading *Teacher:
     * Hattie Wolford* described half the screen. The rules live beside the
     * booking shape, because what a booking can answer is a fact about a
     * booking.
     */
    .filter((b) => bookingMatches(b, filter))

  return (
    <div className="page-shell px-5 py-8">
      <ScheduleHeader
        eyebrow="Master calendar"
        label={data ? formatRange(data.weekStart, data.weekEnd) : 'Loading...'}
        anchor={anchor}
        view="week"
        onPick={setAnchor}
        onView={(view) => onView(view, anchor)}
        onStep={(d) => setAnchor(addDays(anchor, d * 7))}
        onToday={() => setAnchor(today())}
        actions={actions}
        hideTeachers={Boolean(onView)}
        subtitle={
          data && (
            <p className="mt-1 text-sm text-ink-3">
              {data.totalEntries} {data.totalEntries === 1 ? 'session' : 'sessions'}
              {data.hiddenByFilter > 0 && <span> &middot; {data.hiddenByFilter} hidden by filters</span>}
            </p>
          )
        }
      />

      <CalendarFilters
        options={options}
        filter={filter}
        onChange={setFilter}
        matched={data?.totalEntries ?? 0}
        total={data?.unfilteredTotal ?? 0}
      />

      {isError && (
        <ErrorNote boxed>
          <p className="font-semibold">Couldn&rsquo;t load the calendar.</p>
          <p className="mt-1">{(error as Error).message}</p>
        </ErrorNote>
      )}

      {isPending && <p className="text-sm text-ink-3">Loading the week...</p>}

      {/*
        An empty week says why. The season runs September to June, so most of
        the calendar year is legitimately blank — and seven empty boxes in
        August read as lost data rather than "term has not started" (D-100).
      */}
      {/*
        Suppressed by a booking, but **only the kinds a booking contradicts**
        (D-372).

        `explainEmptyWeek` counts *class* entries, so a week whose only thing is
        a private is "empty" by that measure — and *"the season has not started
        yet, the first class is …"* sitting above a card plainly contradicts
        itself.

        **`filtered` is the exception and suppressing it was a bug.** That one
        says *"5 sessions are scheduled this week — clear a filter to see
        them"*, which a private does not contradict at all. Hiding it left a
        teacher who had filtered to one room looking at a grid of other
        people's privates, never told that her own five classes were the thing
        being hidden. The sentence that hands her the way out is the one that
        must survive.
      */}
      {data && empty && shouldExplainEmptyWeek(empty, held.length) && (
        <EmptyWeekNotice reason={empty} onJump={setAnchor} />
      )}

      {data && (
        /*
         * Seven equal columns on desktop; a single stacked column on narrow
         * screens. A week grid does not survive a 390px phone, so it becomes a
         * scrollable day list rather than a squeezed grid.
         */
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4 xl:grid-cols-7">
          {data.days.map((day) => {
            const label = formatDayLabel(day.date)
            const isToday = day.date === today()

            return (
              <section
                key={day.date}
                className={`rounded-xl border bg-surface-2 p-2.5 ${
                  isToday ? 'border-accent' : 'border-line'
                }`}
              >
                <CalendarLink
                  to={withFilter(`/calendar/day/${day.date}`, searchParams)}
                  onActivate={onView ? () => onView("day", day.date) : undefined}
                  className="mb-2.5 flex w-full items-baseline gap-2 px-1 text-left no-underline hover:underline"
                  title="Open the room grid for this day"
                >
                  <span
                    className={`text-[11px] font-bold tracking-wide uppercase ${
                      isToday ? 'text-accent' : 'text-ink-3'
                    }`}
                  >
                    {label.weekday}
                  </span>
                  <span className="tabular text-lg font-semibold">{label.day}</span>
                  <span className="text-[11px] text-ink-3">{label.month}</span>
                </CalendarLink>

                {(() => {
                  // The same list the notice guard reads, narrowed to this day
                  // — a released room is on nobody's schedule (D-372).
                  const onThisDay = held.filter((b) => b.date === day.date)

                  /*
                   * Classes go with it (D-649). She asked to see just her
                   * privates, and a week that kept Tuesday ballet alongside
                   * them would be answering a different question.
                   */
                  // Paired against the bookings this week actually draws, never
                  // the studio's: a colleague's booking for her own child's
                  // private is not drawn for her, so its session must be (D-733).
                  const classes = filter.privatesOnly ? [] : withoutShownPrivates(day.entries, onThisDay)

                  // "No classes" is now only honest when there is no booking
                  // either — that sentence over a day holding a private is the
                  // report this fixes.
                  if (classes.length === 0 && onThisDay.length === 0) {
                    return <p className="px-1 py-3 text-[12px] text-ink-3">Nothing on</p>
                  }

                  return (
                    <div className="flex flex-col gap-2">
                      {classes.map((entry) => (
                        <ClassCard
                          key={entry.occurrenceId}
                          entry={entry}
                          onOpen={setRosterId}
                          weekMixes={!filter.teachingOnly && !filter.wholeStudio}
                        />
                      ))}
                      {onThisDay.map((slot) => (
                        <BookingCard
                          key={slot.id}
                          slot={slot}
                          onOpen={mayOpenBooking(slot) ? openBooking : undefined}
                        />
                      ))}
                    </div>
                  )
                })()}
              </section>
            )
          })}
        </div>
      )}

      {editingBooking && (
        <BookingForm
          roomId={editingBooking.roomId}
          date={editingBooking.date}
          startMinutes={0}
          existing={editingBooking}
          onClose={() => setEditingBooking(null)}
        />
      )}

      {rosterId && (
        <RosterModal
          kind="class"
          id={rosterId}
          onClose={() => setRosterId(null)}
          onOpenSession={setRosterId}
          onEditBooking={canBookRooms ? editBookingById : undefined}
        />
      )}
    </div>
  )
}
